import type { Row, Table } from '@tanstack/react-table';
import { useEffect, useRef } from 'react';
import clsx from 'clsx';

export interface IDataTableSelectCheckboxProps<TData = any> {
  /** TanStack Table instance. Kolon header/cell render içinden table alınır. */
  table: Table<TData>;
  /** Satır checkbox'ı için row verilir. Verilmezse header "tümünü seç" checkbox'ı render edilir. */
  row?: Row<TData>;
  /** Checkbox wrapper için ek CSS sınıfı */
  className?: string;
  /** Checkbox input için ek CSS sınıfı */
  inputClassName?: string;
  /** Checkbox'ı devre dışı bırakır (örn. boş sayfada header checkbox) */
  disabled?: boolean;
}

/**
 * DataTableSelectCheckbox - Satır seçimi için checkbox utility component
 *
 * DataTable'da enableRowSelection: true iken kolon tanımında kullanılır.
 * - row verilmezse: header'da "tümünü seç / tümünü kaldır" checkbox'ı
 * - row verilirse: ilgili satırın seçim checkbox'ı
 *
 * Seçili veriler ref üzerinden tableRef.current.getSelectedRows() ile alınır.
 *
 * @example
 * ```tsx
 * import { createDataTableSelectColumn, DataTableSelectCheckbox } from '@/components/DataTable';
 *
 * const columns = [
 *   createDataTableSelectColumn(),
 *   { accessorKey: 'name', header: 'İsim' },
 *   ...
 * ];
 *
 * // veya manuel kolon:
 * {
 *   id: 'select',
 *   header: ({ table }) => <DataTableSelectCheckbox table={table} />,
 *   cell: ({ row, table }) => <DataTableSelectCheckbox table={table} row={row} />,
 *   meta: { columnClassName: 'w-25px' },
 * }
 * ```
 */
export default function DataTableSelectCheckbox<TData = any>({
  table,
  row,
  className,
  inputClassName,
  disabled = false,
}: IDataTableSelectCheckboxProps<TData>) {
  const isHeader = row == null;
  const checked = isHeader ? table.getIsAllRowsSelected() : row.getIsSelected();
  const indeterminate = isHeader ? table.getIsSomeRowsSelected() : false;

  const handleChange = isHeader
    ? table.getToggleAllRowsSelectedHandler()
    : row!.getToggleSelectedHandler();

  const canSelect = isHeader
    ? table.getRowModel().rows.length > 0
    : row!.getCanSelect();

  const effectiveDisabled = disabled || (!isHeader && !canSelect);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={clsx('form-check form-check-custom form-check-solid justify-content-center', className)}>
      <input
        ref={inputRef}
        type="checkbox"
        className={clsx('form-check-input', inputClassName)}
        checked={checked}
        onChange={handleChange}
        disabled={effectiveDisabled}
        aria-label={isHeader ? 'Tümünü seç' : 'Satırı seç'}
      />
    </div>
  );
}
